import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL || "";

function Badge({ children, tone = "slate" }) {
  const map = {
    red: "bg-rose-100 text-rose-700",
    amber: "bg-amber-100 text-amber-700",
    blue: "bg-blue-100 text-blue-700",
    teal: "bg-teal-100 text-teal-700",
    slate: "bg-slate-100 text-slate-700",
    green: "bg-emerald-100 text-emerald-700",
    critical: "bg-red-100 text-red-700",
    urgent: "bg-amber-100 text-amber-700",
    warning: "bg-yellow-100 text-yellow-700",
    normal: "bg-slate-100 text-slate-600",
  };
  return (
    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${map[tone] || map.slate}`}>
      {children}
    </span>
  );
}

export default function GlobalActionQueuePage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const fetchQueue = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/api/contractor-portal/action-queue`);
        setRows(res.data.rows || []);
      } catch (err) {
        console.error("Failed to fetch action queue:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchQueue();
  }, []);

  const filteredRows = rows.filter((row) => {
    if (filter === "all") return true;
    if (filter === "critical") return row.urgency_level === "critical";
    if (filter === "urgent") return row.urgency_level === "urgent" || row.urgency_level === "critical";
    if (filter === "incomplete") return !row.can_complete;
    return true;
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-700 to-teal-600 text-white">
        <div className="mx-auto max-w-7xl px-6 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-semibold">Action Queue</h1>
              <p className="mt-1 text-sm text-teal-100">
                Everything requiring action across the live system.
              </p>
            </div>
            <div className="flex items-center gap-2 rounded-2xl bg-white/10 px-4 py-2">
              <span className="text-2xl font-bold">{rows.length}</span>
              <span className="text-sm text-teal-100">Active Jobs</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mx-auto max-w-7xl px-6 py-4">
        <div className="flex items-center gap-2">
          {[
            { key: "all", label: "All Jobs" },
            { key: "critical", label: "Critical" },
            { key: "urgent", label: "Urgent" },
            { key: "incomplete", label: "Incomplete" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                filter === f.key
                  ? "bg-teal-600 text-white"
                  : "bg-white text-slate-600 hover:bg-slate-100"
              }`}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-7xl px-6 pb-8">
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-teal-600 border-t-transparent" />
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="py-12 text-center text-slate-500">
              No jobs found matching the filter.
            </div>
          ) : (
            <div className="overflow-auto">
              <table className="w-full text-left text-sm" data-testid="action-queue-table">
                <thead>
                  <tr className="border-b text-slate-500">
                    <th className="pb-3 font-medium">Job</th>
                    <th className="pb-3 font-medium">Park</th>
                    <th className="pb-3 font-medium">Stage</th>
                    <th className="pb-3 font-medium">Next Action</th>
                    <th className="pb-3 font-medium">Urgency</th>
                    <th className="pb-3 font-medium">Missing</th>
                    <th className="pb-3 font-medium">Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr key={row.job_id} className="border-b last:border-b-0 hover:bg-slate-50">
                      <td className="py-3">
                        <Link
                          to={`/contractor/job/${row.job_id}`}
                          className="font-medium text-teal-600 hover:underline"
                        >
                          {row.job_reference}
                        </Link>
                      </td>
                      <td className="py-3 text-slate-700">{row.park_name || "-"}</td>
                      <td className="py-3">
                        <Badge tone="blue">{row.stage || "-"}</Badge>
                      </td>
                      <td className="py-3">
                        <span className="font-medium text-slate-800">
                          {row.next_action?.label || "-"}
                        </span>
                      </td>
                      <td className="py-3">
                        <Badge tone={row.urgency_level || "normal"}>
                          {row.urgency_level || "Normal"}
                        </Badge>
                      </td>
                      <td className="py-3 text-slate-600">
                        {(row.missing_requirements || []).join(", ") || "-"}
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-20 overflow-hidden rounded-full bg-slate-200">
                            <div
                              className="h-full rounded-full bg-teal-600"
                              style={{ width: `${row.completion_progress?.pct || 0}%` }}
                            />
                          </div>
                          <span className="text-xs text-slate-500">
                            {row.completion_progress?.pct || 0}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
